<template>
  <div data-locator="signing-checklist-page">
    <v-row>
      <v-col cols="12" class="px-1 px-sm-3">
        <h1>{{ $t("project.views.SigningChecklist.title") }}</h1>
        <div class="mt-3 mb-0 text-accent-lighten-2">
          <span class="text--medium">
            {{ $t("project.views.SigningChecklist.subtitle") }}
          </span>
          <AppInfoTooltip>
            <p class="text--small">{{ $t('project.views.SigningChecklist.alertContent.part1') }}</p>
            <span class="text--small">{{ $t('project.views.SigningChecklist.alertContent.part2') }}</span>
          </AppInfoTooltip>
        </div>
      </v-col>
    </v-row>
    <div v-if="signingChecklistPending">
      <v-skeleton-loader class="mt-4" type="table-thead, table-heading, table-tbody" />
    </div>

    <div v-if="isSigningChecklistVisible">
      <template v-if="isPm">
        <div class="sticky-container bg-originalGrey-lighten-4">
        <v-row v-if="$vuetify.display.mdAndUp" class="justify-space-between bg-originalGrey-lighten-4">
          <v-col cols="12" md="auto">
            <v-row class="align-center">
                <v-col cols="auto">
                  <SearchTextField v-model="search" :tooltipText="$t('project.views.SigningChecklist.searchPlaceholder')" />
                </v-col>
                <v-col cols="auto">
                  <AppButton color="white" data-locator="filters-button" @click="onOpenFiltersPane">
                    <font-awesome-icon :icon="['far', 'sliders']" class="mr-2" />
                    {{ $t('common.filters') }}
                  </AppButton>
                </v-col>
            </v-row>
          </v-col>

          <v-col cols="12" md="auto">
            <v-row align="center">
              <v-col>
                <v-menu>
                  <template v-slot:activator="{ props }">
                    <AppButton color="white" v-bind="props" data-locator="options-menu-button">
                      {{ $t("project.views.SigningChecklist.optionsHeader") }}
                      <v-icon end>fal fa-chevron-down</v-icon>
                    </AppButton>
                  </template>
                  <v-list data-locator="signing-checklist-options-menu">
                    <v-list-item v-if="!currentRoom.inClosing" :to="{ name: SIGNING_CHECKLIST_ORDER_ROUTE_NAME }" data-locator="signing-order-menu-item">
                      <v-list-item-title>{{ $t("project.views.SigningChecklist.signatureOrderOption") }}
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item v-if="hasPdfSignature" @click="openDialog = true" data-locator="signature-pages-menu-item">
                      <v-list-item-title>
                        {{ $t("project.views.SigningChecklist.signaturePages") }}
                      </v-list-item-title>
                    </v-list-item>
                    <AppDownloadFile :link="exportToPDFLink" data-locator="pdf-export-menu-item">
                      <template #default="{submitForm}">
                        <v-list-item @click="submitForm">
                          <v-list-item-title>{{ $t("project.views.SigningChecklist.exportToPDF") }}</v-list-item-title>
                        </v-list-item>
                      </template>
                    </AppDownloadFile>
                  </v-list>
                </v-menu>
              </v-col>

              <v-col v-if="isPm && !currentRoom.inClosing" class="py-1">
                <AppMenu open-on-hover
                         :menu="signingMenuOptions"
                         :disabled="!hasAtLeastOneDraftEnvelope"
                         data-locator="signing-checklist-start-closing-menu"
                >
                  <template #activator="{ props }">
                    <v-tooltip location="top" :disabled="hasAtLeastOneDraftEnvelope">
                      <template #activator="{ props: tooltipProps }">
                        <div v-bind="tooltipProps">
                          <AppButton
                            :color="hasAtLeastOneDraftEnvelope ? 'primary' : 'grey-300'"
                            variant="outlined"
                            data-locator="start-closing-button"
                            :disabled="currentRoom.inClosing"
                            :dark="!currentRoom.inClosing"
                            v-bind="props"
                          >
                            <font-awesome-icon
                              class="mr-4"
                              :icon="['fas', 'play-circle']"
                              size="lg"
                            />
                            <span class="mr-4">{{ $t("project.views.SigningChecklist.startClosing") }}</span>
                            <font-awesome-icon :icon="['fal', 'chevron-down']" size="lg" />
                          </AppButton>
                        </div>
                      </template>
                      <span>{{$t('project.views.SigningChecklist.startSigningDisabledReason')}}</span>
                    </v-tooltip>
                  </template>
                </AppMenu>
              </v-col>

              <v-col class="py-1">
                <AppMenu open-on-hover :menu="addMenuOptions" data-locator="signing-checklist-add-menu">
                  <template #activator="{ props }">
                    <AppButton
                      color="primary"
                      dark
                      v-bind="props"
                      data-locator="add-menu-button"
                    >
                      <font-awesome-icon
                        class="mr-4"
                        :icon="['far', 'plus']"
                        size="lg"
                      ></font-awesome-icon>
                      <span class="mr-4">{{ $t("project.views.SigningChecklist.newDocumentShort") }}</span>
                      <font-awesome-icon
                        :icon="['fal', 'chevron-down']"
                        size="lg"
                      ></font-awesome-icon>
                    </AppButton>
                  </template>
                </AppMenu>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-row v-if="$vuetify.display.smAndDown" class="mb-4">
          <v-col cols="6" class="px-1 px-sm-3">
            <AppMenu :menu="addMenuOptions">
              <template v-slot:activator="{ props }">
                <AppButton
                  color="primary"
                  dark
                  class="mr-4"
                  v-bind="props"
                  data-locator="add-menu-button"
                >
                  <font-awesome-icon
                    class="mr-2"
                    :icon="['far', 'plus']"
                    size="lg"
                  ></font-awesome-icon>
                  <span>{{ $t("project.views.SigningChecklist.newDocumentShort") }}</span>
                  <font-awesome-icon
                    class="ml-2"
                    :icon="['fal', 'chevron-down']"
                    size="lg"
                  ></font-awesome-icon>
                </AppButton>
              </template>
            </AppMenu>
          </v-col>
          <v-col v-if="isPm && !currentRoom.inClosing && !currentRoom.closingScheduledAt" cols="6" class="px-1 px-sm-3">
            <AppMenu :disabled="!hasAtLeastOneDraftEnvelope" :menu="signingMenuOptions">
              <template v-slot:activator="{ props }">
                <v-tooltip location="top" :disabled="hasAtLeastOneDraftEnvelope">
                  <template #activator="{props: tooltipProps}">
                    <div v-bind="tooltipProps" class="text-right">
                      <AppButton
                        outlined
                        v-bind="props"
                      >
                        <font-awesome-icon
                          class="mr-2"
                          :icon="['far', 'play']"
                          size="lg"
                        ></font-awesome-icon>
                        <span>{{ $t("project.views.SigningChecklist.startClosingShort") }}</span>
                        <font-awesome-icon
                          class="ml-2"
                          :icon="['fal', 'chevron-down']"
                          size="lg"
                        ></font-awesome-icon>
                      </AppButton>
                    </div>
                  </template>
                  <span>{{ $t('project.views.SigningChecklist.startSigningDisabledReason') }}</span>
                </v-tooltip>
              </template>
            </AppMenu>
          </v-col>
          <v-col class="px-1 px-sm-3">
            <v-menu>
              <template v-slot:activator="{ props }">
                <AppButton color="white" v-bind="props">
                  <span>{{ $t("project.views.SigningChecklist.optionsHeader") }}</span>
                  <font-awesome-icon
                    class="ml-2"
                    :icon="['fal', 'chevron-down']"
                    size="lg"
                  ></font-awesome-icon>
                </AppButton>
              </template>
              <v-list>
                <v-list-item v-if="!currentRoom.inClosing" :to="{ name: SIGNING_CHECKLIST_ORDER_ROUTE_NAME }">
                  <v-list-item-title>{{ $t("project.views.SigningChecklist.signatureOrderOption") }}</v-list-item-title>
                </v-list-item>
                <v-list-item v-if="hasPdfSignature" @click="openDialog = true">
                  <v-list-item-title>
                    {{ $t("project.views.SigningChecklist.signaturePages") }}
                  </v-list-item-title>
                  </v-list-item>
                <AppDownloadFile :link="exportToPDFLink">
                  <template #default="{submitForm}">
                    <v-list-item @click="submitForm">
                      <v-list-item-title>{{ $t("project.views.SigningChecklist.exportToPDF") }}</v-list-item-title>
                    </v-list-item>
                  </template>
                </AppDownloadFile>
              </v-list>
            </v-menu>
          </v-col>

          <v-col v-if="isPm && currentRoom.inClosing" cols="6">
            <AppButton
              color="grey-800"
              dark
              @click="cancelClosingModal = true"
            >
              <font-awesome-icon
                class="mr-2"
                :icon="['far', 'ban']"
                size="lg"
              ></font-awesome-icon>
              {{ $t("project.views.SigningChecklist.stopSigningShort") }}
            </AppButton>
          </v-col>
        </v-row>

        <v-row v-if="selectedSteps.length !== 0" class="mt-n2 bg-originalGrey-lighten-4">
          <v-col>
            <v-row no-gutters class="selected-items-bar my-4" data-locator="multi-select-bar">
              <div :class="{'d-flex': $vuetify.display.smAndDown}">
                <span class="mr-2 selected-items-count" v-t="{ path: 'common.selectedItems', plural: selectedSteps.length }" />
                <AppButton
                  class="mr-10"
                  text
                  color="primary"
                  data-locator="unselect-all-bar-item"
                  @click="SET_SELECTED_STEPS([])"
                >
                  {{ $t("common.clearSelection") }}
                </AppButton>
              </div>
              <v-tooltip location="top" :disabled="!disableSendMultiple">
                <template #activator="{ props: tooltipProps }">
                  <div v-bind="tooltipProps">
                    <MultiSelectBarButton
                      v-if="canSendMultiple"
                      :disabled="disableSendMultiple"
                      data-locator="send-to-sign-bar-item"
                      @click="showSendSignStepMultiple"
                    >
                      <font-awesome-icon
                        class="mr-2"
                        :icon="['fas', 'file-signature']"
                      ></font-awesome-icon>
                      {{ $t("project.views.SigningChecklist.selection.send") }}
                    </MultiSelectBarButton>
                  </div>
                </template>
                <span>{{$t('project.views.SigningChecklist.startMultipleSigningDisabledReason', {'nb': SEND_SIGN_STEP_LIMIT})}}</span>
              </v-tooltip>
              <MultiSelectBarButton
                v-if="canValidateMultiple"
                data-locator="validate-then-send-bar-item"
                @click="showSendSignStepMultiple"
              >
                <font-awesome-icon
                  class="mr-2"
                  :icon="['fas', 'file-signature']"
                ></font-awesome-icon>
                {{ $t("project.views.SigningChecklist.selection.validateThenSend") }}
              </MultiSelectBarButton>
              <MultiSelectBarButton data-locator="settings-bar-item"  @click="goToMultipleSettings">
                <font-awesome-icon
                  class="mr-2"
                  :icon="['fas', 'gear']"
                ></font-awesome-icon>
                {{ $t("project.views.SigningChecklist.selection.settings") }}
              </MultiSelectBarButton>
              <MultiSelectBarButton
              v-if="canSwapFileMultiple"
              data-locator="replace-bar-item"
              @click="onMultipleSwap">
                <app-icon class="mr-2" icon-name="exchange"></app-icon>
                {{ $t("project.views.SigningChecklist.selection.swapFile") }}
              </MultiSelectBarButton>
              <MultiSelectBarButton
                v-if="canSignTagsMultiple"
                data-locator="signature-tags-bar-item"
                @click="goToMultipleSignatureTags"
              >
                <font-awesome-icon
                  class="mr-2"
                  :icon="['fas', 'bullseye-arrow']"
                ></font-awesome-icon>
                {{ $t("project.views.SigningChecklist.selection.signatureTags") }}
              </MultiSelectBarButton>
              <MultiSelectBarButton
                v-if="canCancelMultiple"
                data-locator="cancel-bar-item"
                @click="showCancelMultiple"
              >
                <font-awesome-icon
                  class="mr-2"
                  :icon="['fas', 'stop-circle']"
                ></font-awesome-icon>
                {{ $t("project.views.SigningChecklist.selection.cancel") }}
              </MultiSelectBarButton>
              <MultiSelectBarButton
                v-if="canDeleteMultiple"
                data-locator="delete-bar-item"
                @click="showDeleteMultiple"
              >
                <font-awesome-icon
                  class="mr-2"
                  :icon="['fas', 'trash']"
                ></font-awesome-icon>
                {{ $t("project.views.SigningChecklist.selection.delete") }}
              </MultiSelectBarButton>
              <MultiSelectBarButton
                v-if="canResetToDraftMultiple"
                data-locator="reset-to-draft-bar-item"
                @click="showVoidedToDraftStepMultiple"
              >
                <font-awesome-icon
                  class="mr-2"
                  :icon="['fas', 'redo-alt']"
                ></font-awesome-icon>
                {{ $t("project.views.SigningChecklist.selection.voidedToDraft") }}
              </MultiSelectBarButton>
            </v-row>
          </v-col>
        </v-row>
      </div>
      </template>

      <SigningChecklistFiltersActive />

      <v-row class="SigningChecklistTable-container">
        <v-col>
          <AppCard :class="{'pa-0': $vuetify.display.smAndDown}">
            <v-card-text :class="{'px-4': $vuetify.display.smAndDown, 'px-6': $vuetify.display.md, 'px-8': $vuetify.display.lgAndUp}">
              <SigningChecklistSigningInfo v-if="isSigningChecklistInfoVisible"
                                           :mnemo="roomMnemo"
                                           :isSendingDocument="isSendingDocument"
                                           @editSigningDates="onEditSigningDates"
              />

              <v-row v-if="isPm" class="mb-4" justify="space-between" align="center" no-gutters>
                <!-- We use allowEnvelopeGrouping as key to recreate the component if it gets toggled -->
                <!-- isSigningChecklistFiltered is a v-show because we don't want to recreate the component if it changes -->
                <SigningChecklistEnvelopeStats
                  v-show="!isSigningChecklistFiltered"
                  :key="currentRoom.allowEnvelopeGrouping"
                />
                <AppTooltip v-if="isEnvelopeGroupingVisible"
                            :disabled="roomProvidersPending || !currentRoom.inClosing"
                            max-width="400"
                >
                  <template v-slot:activator="{ props }">
                    <div v-bind="props">
                      <v-switch
                        :model-value="currentRoom.allowEnvelopeGrouping"
                        :disabled="currentRoom.inClosing"
                        class="mt-0 pt-0"
                        data-locator="group-documents-toggle"
                        :data-toggle-state="currentRoom.allowEnvelopeGrouping ? 'enabled' : 'disabled'"
                        readonly
                        :loading="patchRoomPending"
                        :label="$t('project.views.SigningChecklist.envelopeGroupingLabel')"
                        hide-details
                        @click="(!currentRoom.inClosing && !patchRoomPending) && onChangeEnvelopeGrouping()"
                      />
                    </div>
                  </template>
                  <span>{{ $t("project.views.SigningChecklist.envelopeGroupingInClosingDisabled") }}</span>
                </AppTooltip>
              </v-row>

              <!-- This divider is also used to divide stats from the table -->
              <!-- so it needs the stats to be there -->
              <!-- If we don't add a z-index here, we will be covered by the table header -->
              <v-divider
                v-if="isSigningChecklistEnvelopeStatsVisible"
                v-show="!isSigningChecklistFiltered"
                class="position-relative mb-4"
                style="z-index:3">
              </v-divider>
              <SigningChecklistTable :mnemo="roomMnemo"/>
            </v-card-text>
          </AppCard>
        </v-col>
      </v-row>

    <SigningChecklistStartSigningDialog v-if="startSigningDialogIsOpen"
                                        :mnemo="roomMnemo"
                                        :action="startSigningDialogAction"
                                        @close="onStartSigningDialogClose"
    />
    </div>

    <div
      v-else-if="!signingChecklistPending"
      class="d-flex flex-column align-center"
      style="margin-top: 100px;"
    >
      <img
        class="d-block mx-auto mb-8"
        src="@/assets/img/signing-checklist/seance-sign.svg"
        alt="signing checklist"
        width="325"
      />
      <p class="mx-auto text-center noDataText" style="max-width: 250px">
        {{ $t("project.views.SigningChecklist.emptyText") }}
      </p>
      <v-row v-if="isPm" class="my-2 text-center mx-auto">
        <v-col>
          <AppButton
            class="mx-1"
            :to="{ name: SIGNING_CHECKLIST_ADD_ROUTE_NAME }"
            color="primary"
          >
            {{ $t("project.views.SigningChecklist.newDocument") }}
          </AppButton>

          <AppButton
            v-if="isSubPayingUser"
            class="mx-1"
            color="primary"
            @click="onUseDocumentTemplate"
          >
            {{ $t("project.views.SigningChecklist.useDocumentTemplate") }}
          </AppButton>
        </v-col>
      </v-row>
    </div>

    <QualifiedDSSignatureWarningDialog v-if="qualifiedDSWarningDialog.isVisible"
                                       @close="closeQualifiedDSWarningDialog"
                                       @confirm="qualifiedDSWarningDialog.confirmCallback?.()"
    />
    <PdfSigningPmDialogButton :mnemo="roomMnemo" :openDialog="openDialog" v-on:setOpenDialog="setOpenDialog"/>
    <SigningChecklistPane />
    <SigningChecklistFiltersPane :mnemo="roomMnemo"/>
    <EnvelopeTemplatesDialog v-if="isDocumentTemplatesDialogOpen"
                             @close="isDocumentTemplatesDialogOpen = false"
    />
  </div>
</template>

<script>
import * as Sentry from '@sentry/vue'
import { debounce } from 'lodash-es'
import { createNamespacedHelpers, mapState, mapGetters } from 'vuex'

import AppCard from '@/common/AppCard.vue'
import AppDownloadFile from '@/common/AppDownloadFile.vue'
import AppInfoTooltip from '@/common/AppInfoTooltip.vue'
import AppMenu from '@/common/AppMenu.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import AppButton from '@/common/buttons/AppButton.vue'
import MultiSelectBarButton from '@/common/buttons/MultiSelectBarButton.vue'
import { APP_HEADER_HEIGHT } from '@/common/constants'
import SearchTextField from '@/common/SearchTextField.vue'
import { handleElementDimensionResize } from '@/common/utils/dimensionObserver'
import {
  canShowCancelStep,
  canShowDeleteStep,
  canShowResetToDraftStep,
  canShowSendStep,
  canShowSignTagsStep,
  canShowValidateStep,
  canSwapFile,
  findStepParent,
  UPLOAD_SESSION_LIMIT,
} from '@/common/utils/signingChecklist'
import { toast } from '@/common/utils/toast'
import SigningChecklistStartSigningDialog from '@/project/signing-checklist/dialogs/SigningChecklistStartSigningDialog.vue'
import EnvelopeTemplatesDialog from '@/project/signing-checklist/envelope-templates/dialogs/EnvelopeTemplatesDialog.vue'
import SigningChecklistSigningInfo from '@/project/signing-checklist/list/SigningChecklistSigningInfo.vue'
import { START_SIGNING_ACTIONS } from '@/project/signing-checklist/list/startSigningActions'
import SigningChecklistFiltersActive from '@/project/signing-checklist/SigningChecklistFiltersActive.vue'
import SigningChecklistFiltersPane from '@/project/signing-checklist/SigningChecklistFiltersPane.vue'
import {
  ROOM_DOCUMENTS_ROUTE_NAME,
  ROOM_ROUTE_TYPES,
  SIGNING_CHECKLIST_ADD_ROUTE_NAME,
  SIGNING_CHECKLIST_ORDER_ROUTE_NAME,
} from '@/router'
import { PATCH_ROOM } from '@/store/modules/room/action_types'
import {
  CANCEL_SCHEDULED_CLOSING,
  CREATE_TITLE,
  DELETE_SEARCH_RESULTS,
  GET_SEARCH_RESULTS,
  GET_SIGNING_CHECKLIST,
  REFRESH_CLOSING_STATUS,
  REFRESH_SIGNING_CHECKLIST,
  REFRESH_STATUS,
  REFRESH_STEP,
  VALIDATE_THEN_SEND_NOW,
  VALIDATE_THEN_SEND_NOW_MULTIPLE,
} from '@/store/modules/signing-checklist/action_types'
import {
  SET_CURRENT_TITLE_EDIT,
  SET_IS_SENDING_DOCUMENT,
  SET_SIGNING_CHECKLIST,
  SET_PARENT_CHILDREN,
  SET_SELECTED_STEPS,
  SET_DELETE_STEP_MODAL,
  SET_CANCEL_STEP_MODAL,
  SET_SEND_SIGN_STEP_MODAL,
  SET_VOIDED_TO_DRAFT_STEP_MODAL,
  ADD_REFRESH_STEP_PENDING,
  SET_REFRESH_STEP_PENDING_IDS,
  SET_FILTERS_PANE_IS_OPEN,
  SET_SWAP_FILES,
} from '@/store/modules/signing-checklist/mutation_types'

import QualifiedDSSignatureWarningDialog from '../signing-checklist/dialogs/QualifiedDSSignatureWarningDialog.vue'
import SigningChecklistTable from '../signing-checklist/list/SigningChecklistTable.vue'
import SigningChecklistPane from '../signing-checklist/pane/SigningChecklistPane.vue'
import PdfSigningPmDialogButton from '../signing-checklist/pdf-signing/PdfSigningPmDialogButton.vue'
import SigningChecklistEnvelopeStats from '../signing-checklist/SigningChecklistEnvelopeStats.vue'

const { mapActions, mapMutations, mapState: mapSigningCheklistState } = createNamespacedHelpers('signingChecklist')
const { mapActions: mapRoomActions, mapState: mapRoomState } = createNamespacedHelpers('room')
const REFRESH_LOOP_POLLING_DELAY = 30000
const REFRESH_STATUS_POLLING_DELAY = 5000
const REFRESH_CLOSING_STATUS_POLLING_DELAY = 5000
const SEND_SIGN_STEP_LIMIT = 50

export default {
  name: 'SigningChecklist',
  components: {
    SigningChecklistPane,
    AppButton,
    AppCard,
    AppDownloadFile,
    AppInfoTooltip,
    AppMenu,
    AppTooltip,
    EnvelopeTemplatesDialog,
    MultiSelectBarButton,
    PdfSigningPmDialogButton,
    QualifiedDSSignatureWarningDialog,
    SearchTextField,
    SigningChecklistFiltersActive,
    SigningChecklistFiltersPane,
    SigningChecklistSigningInfo,
    SigningChecklistStartSigningDialog,
    SigningChecklistTable,
    SigningChecklistEnvelopeStats,
  },
  provide () {
    return {
      onChangeEnvelopeGrouping: this.onChangeEnvelopeGrouping,
    }
  },
  head () {
    return {
      title: this.$t('project.views.SigningChecklist.title'),
    }
  },
  errorCaptured (error) {
    // Ignore it.
    if (error.message === 'ResizeObserver.observe: Argument 1 is not an object.') {
      return false
    }
  },
  data () {
    return {
      SEND_SIGN_STEP_LIMIT,
      APP_HEADER_HEIGHT_STYLE: APP_HEADER_HEIGHT + 'px',
      SIGNING_CHECKLIST_ADD_ROUTE_NAME,
      SIGNING_CHECKLIST_ORDER_ROUTE_NAME,
      openDialog: false,
      qualifiedDSWarningDialog: {
        isVisible: false,
        confirmCallback: null,
      },
      refreshClosingStatusLoopId: 0,
      refreshLoopId: 0,
      refreshStatusLoopId: 0,
      searchQuery: '',
      skipRecap: false,
      startSigningDialogAction: START_SIGNING_ACTIONS.NOW,
      startSigningDialogIsOpen: false,
      isDocumentTemplatesDialogOpen: false,
      resizeObserverDestroyCallback: null,
    }
  },
  computed: {
    ...mapSigningCheklistState([
      'signingChecklist',
      'signingChecklistPending',
      'createTitlePending',
      'cancelScheduledClosingPending',
      'cancelClosingPending',
      'startClosingPending',
      'startScheduledClosingPending',
      'selectedSteps',
      'refreshStepPendingIds',
      'currentTitleEdit',
      'qualifiedDSWarningIsMuted',
      'isSendingDocument',
    ]),
    ...mapState('user', ['profile']),
    ...mapRoomState(['currentRoom', 'patchRoomPending', 'roomProvidersPending']),
    ...mapGetters('room', ['roomMnemo']),
    ...mapGetters('signingChecklist', ['flatSigningChecklist', 'hasActiveFilter', 'isSigningChecklistFiltered']),
    ...mapGetters('user', ['isSubPayingUser']),
    isPm () {
      return this.$store.getters['room/isCurrentUserPm']
    },
    isEnvelopeGroupingVisible () {
      return import.meta.env.VUE_APP_ENABLE_ENVELOPE_GROUPING &&
        (this.currentRoom.canAllowEnvelopeGrouping === true ||
          this.currentRoom.allowEnvelopeGrouping === true
        ) && !this.isSigningChecklistFiltered
    },
    isSigningChecklistVisible () {
      return !this.signingChecklistPending && this.hasAtLeastOneSigningChecklist
    },
    hasAtLeastOneSigningChecklist () {
      return this.signingChecklist && this.signingChecklist.length !== 0
    },
    hasAtLeastOneDraftEnvelope () {
      return this.flatSigningChecklist.some(item => item.type === 'step' && item.envelope?.status === 'draft')
    },
    hasPdfSignature () {
      return this.flatSigningChecklist.some(item => item.type === 'step' && item.envelope?.provider === 'pdf')
    },
    signingMenuOptions () {
      const menu = []
      if (!this.currentRoom.inClosing) {
        menu.push(
          {
            title: this.$t('project.views.SigningChecklist.startClosingNow'),
            locatorValue: 'start-closing-menu-item',
            action: () => this.startSigning(START_SIGNING_ACTIONS.NOW),
            loading: this.startClosingPending,
          },
        )
      }
      if (!this.currentRoom.closingScheduledAt) {
        menu.push({
          title: this.$t('project.views.SigningChecklist.scheduleClosing'),
          locatorValue: 'schedule-closing-menu-item',
          action: () => this.startSigning(START_SIGNING_ACTIONS.SCHEDULED),
          loading: this.startScheduledClosingPending,
        })
      }
      return menu
    },
    addMenuOptions () {
      const options = [
        {
          title: this.$t('project.views.SigningChecklist.newDocument'),
          locatorValue: 'add-document-menu-item',
          action: () => {
            this.$router.push({ name: SIGNING_CHECKLIST_ADD_ROUTE_NAME })
          },
        },
        {
          title: this.$t('project.views.SigningChecklist.newTitle'),
          locatorValue: 'add-title-menu-item',
          action: async () => {
            const payload = {
              prevId: -1,
              depth: 0,
              title: '',
            }
            const newTitle = await this.CREATE_TITLE({
              mnemo: this.roomMnemo,
              data: payload,
              parent: this.signingChecklist,
            })
            await this.REFRESH_SIGNING_CHECKLIST({
              mnemo: this.roomMnemo,
            })
            this.SET_CURRENT_TITLE_EDIT(newTitle)
          },
          loading: this.createTitlePending,
        },
      ]

      if (this.isSubPayingUser) {
        options.push({
          title: this.$t('project.views.SigningChecklist.useDocumentTemplate'),
          locatorValue: 'use-template-menu-item',
          action: () => this.onUseDocumentTemplate(),
        })
      }

      return options
    },
    canSendMultiple () {
      return this.selectedSteps.every(
        (step) => canShowSendStep(step) || canShowValidateStep(step),
      ) && !this.canValidateMultiple
    },
    disableSendMultiple () {
      return this.selectedSteps.length > SEND_SIGN_STEP_LIMIT
    },
    canValidateMultiple () {
      return this.selectedSteps.every(
        (step) => canShowValidateStep(step) && !step.envelope.missingToSend,
      )
    },
    canSignTagsMultiple () {
      return this.selectedSteps.every(canShowSignTagsStep)
    },
    canResetToDraftMultiple () {
      return this.selectedSteps.every(canShowResetToDraftStep)
    },
    canCancelMultiple () {
      return this.selectedSteps.every(canShowCancelStep)
    },
    canDeleteMultiple () {
      return this.selectedSteps.every(canShowDeleteStep)
    },
    canSwapFileMultiple () {
      return this.selectedSteps.every(canSwapFile) && this.selectedSteps.length <= UPLOAD_SESSION_LIMIT
    },
    exportToPDFLink () {
      return `${import.meta.env.VUE_APP_API_URL}/room/${this.roomMnemo}/downloads/signing-checklist-pdf`
    },
    atLeastOneDraftDocumentRequiresQualifiedSignature () {
      return this.flatSigningChecklist.some(item =>
        item.type === 'step' &&
        item.envelope?.signatureType === 'qualified' &&
        item.envelope?.status === 'draft',
      )
    },
    isSigningChecklistInfoVisible () {
      return this.currentRoom.inClosing || this.currentRoom.closingScheduledAt
    },
    isSigningChecklistEnvelopeStatsVisible () {
      return this.isPm
    },
    search: {
      get () {
        return this.searchQuery
      },
      set: debounce(function (val) {
        this.searchQuery = val
        this.searchChecklist()
      }, 1000),
    },
  },
  watch: {
    flatSigningChecklist (newValue) {
      this.hasSendingStep(newValue)
    },
  },
  async created () {
    if (this.currentRoom.isDataroom) {
      this.$router.replace({ name: ROOM_DOCUMENTS_ROUTE_NAME, params: { roomType: ROOM_ROUTE_TYPES.DATAROOM } })
    }
    await this.GET_SIGNING_CHECKLIST({ mnemo: this.roomMnemo })
    this.refreshLoopId = setTimeout(() => { this.refreshLoop() }, REFRESH_LOOP_POLLING_DELAY)
    this.refreshStatusLoopId = setTimeout(() => { this.refreshStatusLoop() }, REFRESH_STATUS_POLLING_DELAY)
    this.REFRESH_CLOSING_STATUS({
      mnemo: this.roomMnemo,
    })
    if (this.isPm) {
      this.refreshClosingStatusLoopId =
        setTimeout(() => { this.refreshClosingStatusLoop() }, REFRESH_CLOSING_STATUS_POLLING_DELAY)
    }

    const receivedOpenDocumentTemplatesDialog = this.$route?.query?.openDocumentTemplatesDialog
    if (receivedOpenDocumentTemplatesDialog) {
      this.isDocumentTemplatesDialogOpen = true
      history.replaceState(null, '', this.$route.path)
    if (this.$route.params.openDocumentTemplatesDialog) { this.isDocumentTemplatesDialogOpen = true }
    }
    await this.$nextTick()
    if (this.isSigningChecklistVisible) {
      this.resizeObserverDestroyCallback =
        handleElementDimensionResize('.sticky-container', 'height', (newHeight) => {
        const headerElement = document.querySelector('.signing-checklist-tab-header')
          if (headerElement) {
            // We add the appbar's height to avoid being hidden by it
            headerElement.style.top = `${newHeight + APP_HEADER_HEIGHT}px`
          }
        })
    }
  },
  beforeUnmount () {
    // Clear timeouts first. We want to avoid getting stuck with a zombie timer.
    clearTimeout(this.refreshLoopId)
    clearTimeout(this.refreshStatusLoopId)
    clearTimeout(this.refreshClosingStatusLoopId)
    // Set to a recognizable value so that we know if we should stop refreshing if already fired
    this.refreshLoopId = -1
    this.refreshStatusLoopId = -1
    this.refreshClosingStatusLoopId = -1
    // Make it empty again
    this.SET_SIGNING_CHECKLIST([])
    this.SET_SELECTED_STEPS([])
    this.DELETE_SEARCH_RESULTS()
    if (this.resizeObserverDestroyCallback) {
      this.resizeObserverDestroyCallback()
    }
  },
  methods: {
    ...mapMutations([SET_FILTERS_PANE_IS_OPEN, SET_SIGNING_CHECKLIST, SET_CURRENT_TITLE_EDIT, SET_PARENT_CHILDREN, SET_SELECTED_STEPS, SET_CANCEL_STEP_MODAL, SET_DELETE_STEP_MODAL, SET_SEND_SIGN_STEP_MODAL, SET_VOIDED_TO_DRAFT_STEP_MODAL, ADD_REFRESH_STEP_PENDING, SET_REFRESH_STEP_PENDING_IDS, SET_IS_SENDING_DOCUMENT, SET_SWAP_FILES]),
    ...mapActions([
      DELETE_SEARCH_RESULTS,
      GET_SEARCH_RESULTS,
      GET_SIGNING_CHECKLIST,
      CREATE_TITLE,
      REFRESH_SIGNING_CHECKLIST,
      REFRESH_STATUS,
      REFRESH_CLOSING_STATUS,
      CANCEL_SCHEDULED_CLOSING,
      VALIDATE_THEN_SEND_NOW,
      VALIDATE_THEN_SEND_NOW_MULTIPLE,
      REFRESH_STEP,
    ]),
    ...mapRoomActions([PATCH_ROOM]),
    startSigning (startSigningAction) {
      this.startSigningDialogAction = startSigningAction
      const shouldWarnAboutQualifiedSignature = this.atLeastOneDraftDocumentRequiresQualifiedSignature && !this.qualifiedDSWarningIsMuted
      if (shouldWarnAboutQualifiedSignature) {
        this.qualifiedDSWarningDialog = {
          isVisible: true,
          confirmCallback: () => {
            this.closeQualifiedDSWarningDialog()
            this.startSigningDialogIsOpen = true
          },
        }
      } else {
        this.startSigningDialogIsOpen = true
      }
    },
    onEditSigningDates () {
      this.startSigningDialogAction = START_SIGNING_ACTIONS.EDIT
      this.startSigningDialogIsOpen = true
    },
    onStartSigningDialogClose () {
      this.startSigningDialogAction = START_SIGNING_ACTIONS.NOW
      this.skipRecap = false
      this.startSigningDialogIsOpen = false
    },
    onMultipleSwap () {
      const stepsIds = this.selectedSteps.map((step) => step.id)

      this.SET_SWAP_FILES({
        ids: stepsIds,
        steps: this.selectedSteps,
      })
    },
    goToMultipleSettings () {
      this.$router.push({
        name: SIGNING_CHECKLIST_ADD_ROUTE_NAME,
        query: {
          step: 2,
          stepIds: this.selectedSteps.map((step) => step.id).toString(),
        },
      })
    },
    goToMultipleSignatureTags () {
      this.$router.push({
        name: SIGNING_CHECKLIST_ADD_ROUTE_NAME,
        query: {
          step: 3,
          stepIds: this.selectedSteps.map((step) => step.id).toString(),
        },
      })
    },
    hasSendingStep (newValue) {
      let firstDocumentIsSending = this.isSendingDocument
      if (this.isSendingDocument && newValue.some(item => item.type === 'step' && (item.envelope.status === 'sent'))) {
        firstDocumentIsSending = false
      }
      const documentsAreSending = newValue.some(item => item.type === 'step' && (item.envelope.status === 'sending' || item.envelope.status === 'queued'))
      this.SET_IS_SENDING_DOCUMENT(documentsAreSending || firstDocumentIsSending)
    },
    showDeleteMultiple () {
      this.SET_DELETE_STEP_MODAL(this.selectedSteps.map((step) => step.id))
    },
    showCancelMultiple () {
      this.SET_CANCEL_STEP_MODAL({
        steps: this.selectedSteps,
        ids: this.selectedSteps.map((step) => step.id),
      })
    },
    showSendSignStepMultiple () {
      const setSendSignStepModalFn = () => this.SET_SEND_SIGN_STEP_MODAL({
        ids: this.selectedSteps.map((step) => step.id),
        steps: this.selectedSteps,
      })

      const shouldWarnAboutQualifiedSignature = this.selectedSteps.some(step => step.envelope?.signatureType === 'qualified') && !this.qualifiedDSWarningIsMuted
      if (shouldWarnAboutQualifiedSignature) {
        this.qualifiedDSWarningDialog = {
          isVisible: true,
          confirmCallback: () => {
            this.closeQualifiedDSWarningDialog()
            setSendSignStepModalFn()
          },
        }
      } else {
        setSendSignStepModalFn()
      }
    },
    showVoidedToDraftStepMultiple () {
      this.SET_VOIDED_TO_DRAFT_STEP_MODAL({
        ids: this.selectedSteps.map((step) => step.envelope.id),
        steps: this.selectedSteps,
      })
    },
    async onChangeEnvelopeGrouping () {
      try {
        await this.PATCH_ROOM({
          mnemo: this.roomMnemo,
          data: {
            optAllowEnvelopeGrouping: !this.currentRoom.allowEnvelopeGrouping,
          },
        })
        this.currentRoom.allowEnvelopeGrouping = !this.currentRoom.allowEnvelopeGrouping
      } catch (error) {
        // If we get this error, it means the room is in closing
        if (error.request.status === 400) {
          toast.error(this.$t('project.views.SigningChecklist.envelopeGroupingInClosingDisabled'))
          // Check back the closing status
          this.REFRESH_CLOSING_STATUS()
        } else {
          toast.error(this.$t('common.msgFailErrorOccurred'))
        }
      }
    },
    async refreshLoop () {
      // We don't try to refresh if we're currently editing
      if (!this.currentTitleEdit) {
        // We want to avoid a zombie timer trying to refresh for a non-current mnemo
        // This should always be true...
        if (this.roomMnemo === this.$store.getters['room/roomMnemo']) {
          const nbSteps = this.flatSigningChecklist.filter((step) => step.type === 'step').length
          const nbTitles = this.flatSigningChecklist.filter((step) => step.type === 'title' && step.display !== 0).length
          try {
            await this.REFRESH_SIGNING_CHECKLIST({
              mnemo: this.roomMnemo,
              queryObject: {
                ifRecent: true,
                nbS: nbSteps,
                nbT: nbTitles,
              },
              isPolling: true,
            })
          } finally {
            if (this.refreshLoopId !== -1) {
              this.refreshLoopId = setTimeout(() => { this.refreshLoop() }, REFRESH_LOOP_POLLING_DELAY)
            }
          }
        } else {
          // This shouldn't happen, the timer has gone wild, the component is a zombie
          // Try to exorcise it ?
          clearTimeout(this.refreshLoopId)
          // Report this. This is unnatural
          Sentry.captureMessage('🧟 Trying to refresh a signing checklist that doesn\'t match the current mnemo', {
            tags: {
              propMnemo: this.roomMnemo,
              storeMnemo: this.$store.getters['room/roomMnemo'],
            },
          })
        }
      } else if (this.refreshStatusLoop !== -1) {
        this.refreshLoopId = setTimeout(() => { this.refreshLoop() }, REFRESH_LOOP_POLLING_DELAY)
      }
    },
    async refreshStatusLoop () {
      // We don't try to refresh if we're currently editing
      if (!this.currentTitleEdit) {
        // We want to avoid a zombie timer trying to refresh for a non-current mnemo
        // This should always be true...
        if (this.roomMnemo === this.$store.getters['room/roomMnemo']) {
          // Sometimes the timer does not kill itself properly and there's no signing checklist anymore
          // In those cases we don't want to do anything else and also kill the timer, again.
          // With fire, this time.
          if (this.signingChecklist.length === 0) {
            clearTimeout(this.refreshStatusLoopId)
            return
          }
          try {
            const data = await this.REFRESH_STATUS(this.roomMnemo)
            // We could have gotten out of the signing checklist in the meantime
            // We still don't want to do anything in that case
            if (this.signingChecklist.length === 0) {
              clearTimeout(this.refreshStatusLoopId)
              return
            }
            // We don't update either if we're currently editing right now
            if (!this.currentTitleEdit && Array.isArray(data)) {
              for (const step of data) {
                const parent = findStepParent(step, this.signingChecklist)
                if (parent) {
                  const shallowCopy = parent.children.slice()
                  const insertIndex = shallowCopy.findIndex((stepFromChildren) => step.id === stepFromChildren.id)
                  shallowCopy.splice(insertIndex, 1, step)
                  this.SET_PARENT_CHILDREN({
                    parent: parent,
                    children: shallowCopy,
                  })
                }
              }
            }
          } finally {
            if (this.refreshStatusLoopId !== -1) {
              this.refreshStatusLoopId = setTimeout(() => { this.refreshStatusLoop() }, REFRESH_STATUS_POLLING_DELAY)
            }
          }
        } else {
          // This shouldn't happen, the timer has gone wild, the component is a zombie
          // Try to exorcise it ?
          clearTimeout(this.refreshStatusLoopId)
          // Report this. This is unnatural
          Sentry.captureMessage('🧟 Trying to refresh the statuses of a signing checklist that doesn\'t match the current mnemo', {
            tags: {
              propMnemo: this.roomMnemo,
              storeMnemo: this.$store.getters['room/roomMnemo'],
            },
          })
        }
      } else if (this.refreshStatusLoopId !== -1) {
        this.refreshStatusLoopId = setTimeout(() => { this.refreshStatusLoop() }, REFRESH_STATUS_POLLING_DELAY)
      }
    },
    async refreshClosingStatusLoop () {
      // We want to avoid a zombie timer trying to refresh for a non-current mnemo
      // This should always be true...
      if (this.roomMnemo === this.$store.getters['room/roomMnemo']) {
        try {
          await this.REFRESH_CLOSING_STATUS({
            mnemo: this.roomMnemo,
            isPolling: true,
          })
        } finally {
          if (this.refreshClosingStatusLoopId !== -1) {
            this.refreshClosingStatusLoopId = setTimeout(() => { this.refreshClosingStatusLoop() }, REFRESH_CLOSING_STATUS_POLLING_DELAY)
          }
        }
      } else {
        // This shouldn't happen, the timer has gone wild, the component is a zombie
        // Try to exorcise it ?
        clearTimeout(this.refreshClosingStatusLoopId)
        // Report this. This is unnatural
        Sentry.captureMessage('🧟 Trying to refresh the closing status of a signing checklist that doesn\'t match the current mnemo', {
          tags: {
            propMnemo: this.roomMnemo,
            storeMnemo: this.$store.getters['room/roomMnemo'],
          },
        })
      }
    },
    setOpenDialog (value) {
      this.openDialog = value
    },
    onOpenFiltersPane () {
      this.SET_FILTERS_PANE_IS_OPEN(true)
    },
    searchChecklist () {
      this.search ? this.GET_SEARCH_RESULTS(this.searchQuery) : this.DELETE_SEARCH_RESULTS()
    },
    onUseDocumentTemplate () {
      this.isDocumentTemplatesDialogOpen = true
    },
    closeQualifiedDSWarningDialog () {
      this.qualifiedDSWarningDialog = {
        isVisible: false,
        confirmCallback: null,
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import "vuetify/settings";

.step {
  border-top: 1px solid black;
}

.signingChecklist-sticky {
  background-color: rgb(var(--v-theme-originalGrey-lighten-4));
}

:deep(.c-affix.is-fixed) {
  background-color: rgb(var(--v-theme-originalGrey-lighten-4));
  z-index: 5 !important;
}
.SigningChecklistTable-container {
  @media #{map-get($display-breakpoints, 'sm-and-down')} {
    margin-left: -20px;
    margin-right: -20px;
  }
}
.sticky-container {
  position: sticky;
  // We start at the appbar's height to avoid being hidden by it
  top: v-bind(APP_HEADER_HEIGHT_STYLE);
  z-index: 2;
}
</style>
